// src/context/AppContext.jsx - Updated with Scroll Utilities and IMPROVED Race Condition Handling
import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { usePrivacyAuth } from './PrivacyAuthContext';
import { useProgressStore } from '../stores/progressStore';
import { LEGACY_TOOL_KEYS, loadToolData } from '../utils/toolStorage';
import firebaseAnalyticsService from '../services/analytics/FirebaseAnalyticsService';

// Simple storage fallback if service fails
const simpleStorage = {
  save: (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Simple storage save failed:', error);
      return false;
    }
  },
  load: (key) => {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Simple storage load failed:', error);
      return null;
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Simple storage remove failed:', error);
      return false;
    }
  },
};

// The Vibe Check results live under per-account keys ('guest' until there is
// an account). These helpers are the one place that key shape is spelt out for
// the adopt and restore paths below, so the guest scope, the account scope and
// the copy on the auth hub all agree on what "the results" are.
const assessmentKeys = (scope) => ({
  results: `vibe_assessment_results_${scope}`,
  history: `vibe_assessment_history_${scope}`,
});

const readAssessmentScope = (scope) => {
  const keys = assessmentKeys(scope);
  const history = simpleStorage.load(keys.history);
  return {
    results: simpleStorage.load(keys.results),
    history: Array.isArray(history) ? history : [],
  };
};

const writeAssessmentScope = (scope, { results, history }) => {
  const keys = assessmentKeys(scope);
  if (results) simpleStorage.save(keys.results, results);
  if (Array.isArray(history)) simpleStorage.save(keys.history, history);
};

const clearAssessmentScope = (scope) => {
  const keys = assessmentKeys(scope);
  simpleStorage.remove(keys.results);
  simpleStorage.remove(keys.history);
};

// When was this copy of the results last written? A copy that has been
// through the hub carries __updatedAt; one that has only ever lived locally
// has, at best, the completedAt on its latest history entry. 0 means unknown.
const assessmentStamp = ({ results, history } = {}) => {
  const last = Array.isArray(history) && history.length > 0 ? history[history.length - 1] : null;
  return Number(results?.__updatedAt)
    || Date.parse(results?.completedAt || '')
    || Date.parse(last?.completedAt || '')
    || 0;
};

// Saved progress from before October 2026 carries a streak counter
// (userProgress.streaks) that nothing ever advanced. It is dropped on load, so
// the next save writes the progress without it.
const withoutStreaks = (userProgress) => {
  if (!userProgress || typeof userProgress !== 'object') return userProgress;
  const { streaks: _streaks, ...rest } = userProgress;
  return rest;
};

// Mirror the results to the auth hub, debounced, the same way the toolkit
// does. Fire-and-forget: the hub is a mirror, never a dependency.
const mirrorAssessmentToHub = (snapshot) => {
  import('../services/ToolSyncService')
    .then((m) => m.markAssessmentDirty(snapshot))
    .catch(() => { /* local-first: a failed sync is not a failed app */ });
};

// Initial state. No points, levels or streaks: which modules you have finished
// and the insights you have saved live in stores/progressStore.js.
const initialState = {
  // Navigation
  currentView: 'home',
  
  // User progress
  assessmentResults: null,
  assessmentHistory: [], // Store historical assessment results
  completedModules: [],
  completedTools: [],
  userProgress: {
    modules: {},
    tools: {},
    dailyCheckins: [],
  },
  
  // App state
  loading: false,
  error: null,
  lastSync: null,
  
  // Preferences
  preferences: {
    theme: 'dark',
    notifications: true,
    soundEffects: true,
    vibeMode: true,
  },
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CURRENT_VIEW: 'SET_CURRENT_VIEW',
  
  // Assessment
  COMPLETE_ASSESSMENT: 'COMPLETE_ASSESSMENT',
  UPDATE_ASSESSMENT: 'UPDATE_ASSESSMENT',
  RESTORE_ASSESSMENT: 'RESTORE_ASSESSMENT',

  // Modules
  COMPLETE_MODULE: 'COMPLETE_MODULE',
  UPDATE_MODULE_PROGRESS: 'UPDATE_MODULE_PROGRESS',
  
  // Tools
  COMPLETE_TOOL: 'COMPLETE_TOOL',
  UPDATE_TOOL_PROGRESS: 'UPDATE_TOOL_PROGRESS',
  
  // Data Management
  LOAD_USER_DATA: 'LOAD_USER_DATA',
  SAVE_USER_DATA: 'SAVE_USER_DATA',
  RESET_USER_DATA: 'RESET_USER_DATA',
  
  // Preferences
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
};

// Reducer - SIMPLIFIED
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
      
    case ActionTypes.SET_CURRENT_VIEW:
      return { ...state, currentView: action.payload };
      
    case ActionTypes.COMPLETE_ASSESSMENT:
      // Add current results to history before updating
      const newHistoryEntry = {
        ...action.payload,
        completedAt: new Date().toISOString(),
        id: Date.now(),
      };
      
      return {
        ...state,
        assessmentResults: action.payload,
        assessmentHistory: [...(Array.isArray(state.assessmentHistory) ? state.assessmentHistory : []), newHistoryEntry],
      };
      
    case ActionTypes.UPDATE_ASSESSMENT:
      return {
        ...state,
        assessmentResults: { ...state.assessmentResults, ...action.payload },
      };

    case ActionTypes.RESTORE_ASSESSMENT:
      // Results arriving from outside the completion path - adopted from the
      // guest scope on sign-in, or pulled down from the auth hub. Unlike
      // COMPLETE_ASSESSMENT this appends nothing to history: the snapshot
      // carries its own.
      return {
        ...state,
        assessmentResults: action.payload?.results || null,
        assessmentHistory: Array.isArray(action.payload?.history)
          ? action.payload.history
          : state.assessmentHistory,
      };
      
    case ActionTypes.COMPLETE_MODULE:
      return {
        ...state,
        completedModules: [...new Set([...state.completedModules, action.payload.moduleId])],
        userProgress: {
          ...state.userProgress,
          modules: {
            ...state.userProgress.modules,
            [action.payload.moduleId]: {
              ...state.userProgress.modules[action.payload.moduleId],
              completed: true,
              completedAt: new Date().toISOString(),
              progressPercentage: 100,
            },
          },
        },
      };
      
    case ActionTypes.UPDATE_MODULE_PROGRESS:
      return {
        ...state,
        userProgress: {
          ...state.userProgress,
          modules: {
            ...state.userProgress.modules,
            [action.payload.moduleId]: {
              ...state.userProgress.modules[action.payload.moduleId],
              ...action.payload.progress,
              lastUpdated: new Date().toISOString(),
            },
          },
        },
      };
      
    case ActionTypes.COMPLETE_TOOL:
      return {
        ...state,
        completedTools: [...new Set([...state.completedTools, action.payload.toolId])],
        userProgress: {
          ...state.userProgress,
          tools: {
            ...state.userProgress.tools,
            [action.payload.toolId]: {
              ...state.userProgress.tools[action.payload.toolId],
              completed: true,
              completedAt: new Date().toISOString(),
              sessionData: action.payload.sessionData,
            },
          },
        },
      };
      
    case ActionTypes.UPDATE_TOOL_PROGRESS:
      return {
        ...state,
        userProgress: {
          ...state.userProgress,
          tools: {
            ...state.userProgress.tools,
            [action.payload.toolId]: {
              ...state.userProgress.tools[action.payload.toolId],
              ...action.payload.progress,
              lastUpdated: new Date().toISOString(),
            },
          },
        },
      };
      
    case ActionTypes.LOAD_USER_DATA:
      // console.log('📝 AppContext reducer: Loading user data', action.payload);
      return {
        ...state,
        ...action.payload,
        assessmentHistory: Array.isArray(action.payload?.assessmentHistory) ? action.payload.assessmentHistory : [],
        loading: false,
      };
      
    case ActionTypes.SAVE_USER_DATA:
      return {
        ...state,
        lastSync: new Date().toISOString(),
      };
      
    case ActionTypes.RESET_USER_DATA:
      return {
        ...initialState,
        currentView: 'home',
      };
      
    case ActionTypes.UPDATE_PREFERENCES:
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload },
      };
      
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const { user } = usePrivacyAuth();
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Debug user state - DISABLED to prevent console spam
  // console.log('🔍 AppContext user state:', { user: !!user, assessmentResults: !!state.assessmentResults });

  // Load user data on initialization and when user changes
  useEffect(() => {
    loadUserData();
  }, [user]);

  // Let the hub sync hand results back (a browser with none of its own).
  // This module owns the local copy, so it decides whether the hub's is newer;
  // a local copy with an unknown stamp is kept rather than risked.
  useEffect(() => {
    let cancelled = false;
    let unregister = () => {};
    import('../services/ToolSyncService')
      .then((m) => {
        if (cancelled) return;
        unregister = m.registerAssessmentRestoreHook((remote) => {
          const scope = getCurrentUserId() || 'guest';
          const local = readAssessmentScope(scope);
          const remoteAt = Number(remote?.__updatedAt) || 0;
          const localAt = assessmentStamp(local);
          if (local.results && !(localAt > 0 && remoteAt > localAt)) return false;
          const snapshot = {
            results: { ...remote.results, __updatedAt: remoteAt },
            history: Array.isArray(remote.history) ? remote.history : local.history,
          };
          writeAssessmentScope(scope, snapshot);
          dispatch({ type: ActionTypes.RESTORE_ASSESSMENT, payload: snapshot });
          return true;
        });
      })
      .catch(() => { /* sync module unavailable: local-first, carry on */ });
    return () => {
      cancelled = true;
      unregister();
    };
  }, []);

  // Handle URL parameters (e.g., redirect from signup)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');

    console.log('🟢 AppContext: Checking URL params');
    console.log('🟢 URL:', window.location.href);
    console.log('🟢 viewParam:', viewParam);

    if (viewParam) {
      console.log('🟢 Setting view to:', viewParam);

      // Set the view
      dispatch({ type: ActionTypes.SET_CURRENT_VIEW, payload: viewParam });

      // Clear only the `view` parameter AFTER setting the view, and keep
      // the rest of the query string: the Tools page mounts lazily and
      // reads `?tool=` well after this timer fires (it clears that itself).
      setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('view');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }, 100);
    }
  }, []);

  // Memoize saveUserData to prevent infinite loops
  const saveUserDataMemo = useCallback(() => {
    saveUserData();
  }, [user]); // Include user so storage key updates when user changes

  // Auto-save data when state changes (debounced)
  useEffect(() => {
    // Skip if this is just a lastSync update
    if (state.lastSync && (Date.now() - new Date(state.lastSync).getTime()) < 1000) {
      return;
    }

    const timeoutId = setTimeout(() => {
      saveUserDataMemo();
    }, 2000); // Debounce saves by 2 seconds

    return () => clearTimeout(timeoutId);
  }, [state.assessmentResults, state.assessmentHistory, state.completedModules, state.completedTools, state.userProgress, saveUserDataMemo]);

  // =========== SCROLL UTILITIES ===========
  const scrollToTop = useCallback((behavior = 'smooth') => {
    window.scrollTo({ 
      top: 0, 
      behavior, 
    });
  }, []);

  const scrollToElement = useCallback((elementId, behavior = 'smooth', offset = 0) => {
    const element = document.getElementById(elementId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior,
      });
    }
  }, []);

  const scrollToPosition = useCallback((position, behavior = 'smooth') => {
    window.scrollTo({
      top: position,
      behavior,
    });
  }, []);

  // Helper function to get current user ID - Updated to use Firebase auth first
const getCurrentUserId = (authUser = null) => {
  try {
    // First, try to get user ID from Firebase auth if available
    if (authUser && authUser.uid) {
      console.log('🔍 AppContext: Using Firebase user ID:', authUser.uid);
      return authUser.uid;
    }

    // Fallback to localStorage privacy auth
    const authData = localStorage.getItem('vibe_user_identity');
    if (authData) {
      const parsed = JSON.parse(authData);
      console.log('🔍 AppContext: Using localStorage user ID:', parsed.userId);
      return parsed.userId;
    }

    console.log('🔍 AppContext: No user ID found, using guest');
    return null;
  } catch (error) {
    console.warn('🔍 AppContext: Error getting user ID:', error);
    return null;
  }
};

  // Load all user data from storage
const loadUserData = async () => {
  try {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    const currentUserId = getCurrentUserId(user);
    const storageKey = currentUserId ? currentUserId : 'guest';
    
    if (!currentUserId) {
      console.log('📥 Loading guest assessment data...');
    } else {
      console.log('📥 Loading data for user:', currentUserId);
    }

    // Try to load from storage service first, fall back to simple storage
    let assessmentResults = null;
    let assessmentHistory = null;
    let progressData = null;
    let preferences = null;

    try {
      // Try complex storage first with user-specific keys
      const { friendshipsVibeStorage } = await import('../services/storage/FriendshipsVibeStorageService');
      const { STORAGE_CATEGORIES } = await import('../services/storage/StorageCategories');
      
      assessmentResults = await friendshipsVibeStorage.load(
        STORAGE_CATEGORIES.ASSESSMENT.BASE,
        `${STORAGE_CATEGORIES.ASSESSMENT.RESULTS}_${storageKey}`,
      );
      
      assessmentHistory = await friendshipsVibeStorage.load(
        STORAGE_CATEGORIES.ASSESSMENT.BASE,
        `assessment_history_${storageKey}`,
      );
      
      progressData = await friendshipsVibeStorage.load(
        STORAGE_CATEGORIES.PROGRESS.BASE,
        `${STORAGE_CATEGORIES.PROGRESS.MILESTONES}_${storageKey}`,
      );
      
      preferences = await friendshipsVibeStorage.getPreferences();
    } catch (storageError) {
      console.warn('Complex storage failed, using simple storage:', storageError);

      // Fall back to simple localStorage with user-specific or guest keys
      assessmentResults = simpleStorage.load(`vibe_assessment_results_${storageKey}`);
      assessmentHistory = simpleStorage.load(`vibe_assessment_history_${storageKey}`);
      progressData = simpleStorage.load(`vibe_progress_data_${storageKey}`);
      preferences = simpleStorage.load(`vibe_preferences_${storageKey}`);
    }

    // Additional fallback: try direct localStorage access
    if (!assessmentResults) {
      try {
        const stored = localStorage.getItem(`vibe_assessment_results_${storageKey}`);
        if (stored) {
          assessmentResults = JSON.parse(stored);
          console.log(`✅ Found assessment results in direct localStorage with key: vibe_assessment_results_${storageKey}`);
        }
      } catch (error) {
        console.warn('Failed to load from direct localStorage:', error);
      }
    }

    // Same direct fallback for history, which the adopt and restore paths
    // below write under the plain key.
    if (!Array.isArray(assessmentHistory) || assessmentHistory.length === 0) {
      const direct = simpleStorage.load(`vibe_assessment_history_${storageKey}`);
      if (Array.isArray(direct) && direct.length > 0) assessmentHistory = direct;
    }

    // An account with no results of its own, on a browser holding results
    // completed as a guest: those are this person's results. The account
    // usually appears seconds after the results do, when the auth hub session
    // comes up, and without this step the results vanish from the page the
    // person is reading - the opposite of what "signing in keeps your
    // results" promises. Adopt them, and stop the next guest on a shared
    // device inheriting them.
    if (!assessmentResults && storageKey !== 'guest') {
      const guest = readAssessmentScope('guest');
      if (guest.results) {
        writeAssessmentScope(storageKey, guest);
        clearAssessmentScope('guest');
        assessmentResults = guest.results;
        if (!Array.isArray(assessmentHistory) || assessmentHistory.length === 0) {
          assessmentHistory = guest.history;
        }
        mirrorAssessmentToHub(guest);
      }
    }

    // Combine all loaded data
    const userData = {
      assessmentResults,
      assessmentHistory: Array.isArray(assessmentHistory) ? assessmentHistory : [],
      completedModules: progressData?.completedModules || [],
      completedTools: progressData?.completedTools || [],
      userProgress: withoutStreaks(progressData?.userProgress) || initialState.userProgress,
      preferences: preferences || initialState.preferences,
    };

    console.log('📊 Loaded user data:', {
      assessmentExists: !!userData.assessmentResults,
      completedModules: userData.completedModules.length,
      completedTools: userData.completedTools.length,
    });
    
    console.log('📋 Assessment results loaded:', assessmentResults ? 'Yes ✅' : 'No ❌');
    console.log('📋 Raw assessment data:', assessmentResults);
    console.log('📋 Storage key used:', `vibe_assessment_results_${storageKey}`);

    // Ensure assessment results are properly set in state
    if (assessmentResults) {
      console.log('✅ Setting assessment results in AppContext state');
    } else {
      console.log('❌ No assessment results found to load');
    }

    dispatch({ type: ActionTypes.LOAD_USER_DATA, payload: userData });
  } catch (error) {
    console.error('Error loading user data:', error);
    dispatch({ type: ActionTypes.SET_ERROR, payload: 'Failed to load user data' });
    dispatch({ type: ActionTypes.SET_LOADING, payload: false });
  }
};

  // Save all user data to storage
// ALSO UPDATE: Save user data to use user-specific keys
const saveUserData = async () => {
  try {
    const currentUserId = getCurrentUserId(user);
    const storageKey = currentUserId ? currentUserId : 'guest';
    
    if (!currentUserId) {
      console.log('💾 Saving guest assessment data...');
    } else {
      console.log('💾 Saving data for user:', currentUserId);
    }

    // Try complex storage first, fall back to simple storage
    try {
      const { friendshipsVibeStorage } = await import('../services/storage/FriendshipsVibeStorageService');
      const { STORAGE_CATEGORIES } = await import('../services/storage/StorageCategories');
      
      // Save assessment results with user ID or guest
      if (state.assessmentResults) {
        await friendshipsVibeStorage.save(
          STORAGE_CATEGORIES.ASSESSMENT.BASE,
          `${STORAGE_CATEGORIES.ASSESSMENT.RESULTS}_${storageKey}`,
          state.assessmentResults,
        );
      }

      // Save assessment history with user ID or guest
      if (state.assessmentHistory && state.assessmentHistory.length > 0) {
        await friendshipsVibeStorage.save(
          STORAGE_CATEGORIES.ASSESSMENT.BASE,
          `assessment_history_${storageKey}`,
          state.assessmentHistory,
        );
      }

      // Save progress data with user ID or guest
      const progressData = {
        completedModules: state.completedModules,
        completedTools: state.completedTools,
        userProgress: state.userProgress,
        lastUpdated: new Date().toISOString(),
      };

      await friendshipsVibeStorage.save(
        STORAGE_CATEGORIES.PROGRESS.BASE,
        `${STORAGE_CATEGORIES.PROGRESS.MILESTONES}_${storageKey}`,
        progressData,
      );

      // Save preferences with user ID
      if (friendshipsVibeStorage.savePreferences) {
        await friendshipsVibeStorage.savePreferences(state.preferences);
      }
    } catch (storageError) {
      console.warn('Complex storage save failed, using simple storage:', storageError);
      
      // Fall back to simple localStorage with user-specific or guest keys
      if (state.assessmentResults) {
        simpleStorage.save(`vibe_assessment_results_${storageKey}`, state.assessmentResults);
      }
      
      if (state.assessmentHistory && state.assessmentHistory.length > 0) {
        simpleStorage.save(`vibe_assessment_history_${storageKey}`, state.assessmentHistory);
      }
      
      const progressData = {
        completedModules: state.completedModules,
        completedTools: state.completedTools,
        userProgress: state.userProgress,
        lastUpdated: new Date().toISOString(),
      };
      
      simpleStorage.save(`vibe_progress_data_${storageKey}`, progressData);
      simpleStorage.save(`vibe_preferences_${storageKey}`, state.preferences);
    }

    // Remove the dispatch that causes infinite loops
    // dispatch({ type: ActionTypes.SAVE_USER_DATA });
  } catch (error) {
    console.error('Error saving user data:', error);
  }
};

  // Action creators - simplified to prevent infinite re-renders
  const actions = {
    // =========== SCROLL ACTIONS ===========
    scrollToTop,
    scrollToElement,
    scrollToPosition,

    // Navigation
    setCurrentView: useCallback((view) => {
      dispatch({ type: ActionTypes.SET_CURRENT_VIEW, payload: view });

      // Track view change in analytics
      firebaseAnalyticsService.trackScreenView(view, `View_${view}`);
    }, [dispatch]),

    // Assessment
    completeAssessment: async (results) => {
      console.log('💾 AppContext: Completing assessment with results:', results);
      console.log('💾 AppContext: Results type:', typeof results);
      console.log('💾 AppContext: Results keys:', results ? Object.keys(results) : 'null/undefined');

      dispatch({ type: ActionTypes.COMPLETE_ASSESSMENT, payload: results });

      // Force save assessment data immediately with direct localStorage fallback
      console.log('💾 AppContext: Force saving assessment data...');

      // Save immediately to localStorage as backup
      const currentUserId = getCurrentUserId();
      const storageKey = currentUserId ? currentUserId : 'guest';

      console.log('💾 AppContext: currentUserId:', currentUserId);
      console.log('💾 AppContext: storageKey:', storageKey);
      console.log('💾 AppContext: Final storage key:', `vibe_assessment_results_${storageKey}`);

      try {
        const jsonString = JSON.stringify(results);
        console.log('💾 AppContext: JSON string length:', jsonString.length);
        console.log('💾 AppContext: JSON string preview:', jsonString.substring(0, 200) + '...');

        localStorage.setItem(`vibe_assessment_results_${storageKey}`, jsonString);
        console.log(`✅ Assessment saved to localStorage with key: vibe_assessment_results_${storageKey}`);

        // Verify it was saved
        const verification = localStorage.getItem(`vibe_assessment_results_${storageKey}`);
        console.log('✅ Verification - data exists after save:', !!verification);

      } catch (error) {
        console.error('❌ Failed to save assessment to localStorage:', error);
      }

      // Mirror to the auth hub. The reducer has just appended this result to
      // history; rebuild that entry here because the closure's state is from
      // before the dispatch.
      mirrorAssessmentToHub({
        results,
        history: [
          ...(Array.isArray(state.assessmentHistory) ? state.assessmentHistory : []),
          { ...results, completedAt: new Date().toISOString(), id: Date.now() },
        ],
      });

      // Also trigger the normal save process
      setTimeout(() => {
        saveUserData();
      }, 100);
    },

    updateAssessment: (updates) => {
      dispatch({ type: ActionTypes.UPDATE_ASSESSMENT, payload: updates });
    },

    // Results arriving from outside the completion path. `uid` is passed when
    // the caller has just signed the person in and this context has not yet
    // reloaded for them; `adoptGuest` moves guest-scope results into that
    // account so they are still on screen when it does.
    restoreAssessment: (snapshot, { uid = null, adoptGuest = false } = {}) => {
      if (!snapshot?.results) return false;
      const scope = uid || getCurrentUserId(user) || 'guest';
      writeAssessmentScope(scope, snapshot);
      if (adoptGuest && scope !== 'guest') clearAssessmentScope('guest');
      dispatch({ type: ActionTypes.RESTORE_ASSESSMENT, payload: snapshot });
      return true;
    },

    // Migrate guest data when user signs up
    migrateGuestData: async () => {
      try {
        console.log('🔄 Migrating guest data to user account...');
        
        // Load guest data
        const guestAssessment = simpleStorage.load('vibe_assessment_results_guest');
        const guestProgress = simpleStorage.load('vibe_progress_data_guest');
        const guestPreferences = simpleStorage.load('vibe_preferences_guest');
        
        if (guestAssessment || guestProgress || guestPreferences) {
          // Update state with guest data
          if (guestAssessment) {
            dispatch({ type: ActionTypes.COMPLETE_ASSESSMENT, payload: guestAssessment });
            console.log('✅ Migrated guest assessment data');
          }
          
          if (guestProgress) {
            if (guestProgress.completedModules) {
              guestProgress.completedModules.forEach(moduleId => {
                dispatch({ type: ActionTypes.COMPLETE_MODULE, payload: { moduleId } });
              });
            }
            if (guestProgress.completedTools) {
              guestProgress.completedTools.forEach(toolId => {
                dispatch({ type: ActionTypes.COMPLETE_TOOL, payload: toolId });
              });
            }
            console.log('✅ Migrated guest progress data');
          }
          
          if (guestPreferences) {
            dispatch({ type: ActionTypes.UPDATE_PREFERENCES, payload: guestPreferences });
            console.log('✅ Migrated guest preferences');
          }
          
          // Save migrated data with user ID
          await saveUserData();
          
          // Clean up guest data
          simpleStorage.remove('vibe_assessment_results_guest');
          simpleStorage.remove('vibe_progress_data_guest');
          simpleStorage.remove('vibe_preferences_guest');
          
          console.log('🧹 Cleaned up guest data');
        } else {
          console.log('📝 No guest data to migrate');
        }
      } catch (error) {
        console.error('❌ Error migrating guest data:', error);
      }
    },

    // IMPROVED: Complete module with better context checking
    completeModule: async (moduleId, progressData = {}) => {
      console.log('🎯 AppContext: Completing module', moduleId);
      
      // First update local state immediately
      dispatch({ 
        type: ActionTypes.COMPLETE_MODULE, 
        payload: { moduleId, progressData }, 
      });
      
      // Force save user data
      setTimeout(() => {
        saveUserData();
      }, 300);
    },

    // FIXED: Better module progress tracking
    updateModuleProgress: (moduleId, progress) => {
      console.log('AppContext: Updating module progress', moduleId, progress);
      
      dispatch({
        type: ActionTypes.UPDATE_MODULE_PROGRESS,
        payload: { moduleId, progress },
      });
      
      // Don't auto-complete here - it's handled by explicit completeModule calls
    },

    // FIXED: Better progress checking
    getModuleProgress: (moduleId) => {
      const moduleProgress = state.userProgress.modules[moduleId] || { 
        progressPercentage: 0, 
        completed: state.completedModules.includes(moduleId), 
      };
      
      // Ensure consistency between completed modules array and progress object
      if (state.completedModules.includes(moduleId) && !moduleProgress.completed) {
        moduleProgress.completed = true;
        moduleProgress.progressPercentage = 100;
      }
      
      return moduleProgress;
    },

    // FIXED: Better completion checking
    isModuleCompleted: (moduleId) => {
      // Check both the completedModules array AND the progress object
      const inCompletedArray = state.completedModules.includes(moduleId);
      const progressObject = state.userProgress.modules[moduleId];
      const progressCompleted = progressObject?.completed === true || progressObject?.progressPercentage >= 100;
      
      return inCompletedArray || progressCompleted;
    },

    // Tools
    completeTool: async (toolId, sessionData = {}) => {
      dispatch({
        type: ActionTypes.COMPLETE_TOOL,
        payload: { toolId, sessionData },
      });
    },

    updateToolProgress: (toolId, progress) => {
      dispatch({
        type: ActionTypes.UPDATE_TOOL_PROGRESS,
        payload: { toolId, progress },
      });
    },

    getToolProgress: (toolId) => {
      return state.userProgress.tools[toolId] || {};
    },

    isToolCompleted: (toolId) => {
      return state.completedTools.includes(toolId);
    },

    // Micro-session and exercise completion (recorded, never scored)
    completeMicroSession: async (moduleId, sessionIndex, sessionData = {}) => {
      console.log('🎯 AppContext: Completing micro session', moduleId, sessionIndex);
      
      const currentProgress = actions.getModuleProgress(moduleId);
      const updatedSessions = [...(currentProgress.completedMicroSessions || []), sessionIndex];
      
      await actions.updateModuleProgress(moduleId, {
        ...currentProgress,
        completedMicroSessions: updatedSessions,
        lastUpdated: new Date().toISOString(),
      });
    },

    completeExercise: async (moduleId, exerciseIndex, exerciseData = {}) => {
      console.log('🎯 AppContext: Completing exercise', moduleId, exerciseIndex);
      
      const currentProgress = actions.getModuleProgress(moduleId);
      const updatedExercises = [...(currentProgress.completedExercises || []), exerciseIndex];
      
      await actions.updateModuleProgress(moduleId, {
        ...currentProgress,
        completedExercises: updatedExercises,
        lastUpdated: new Date().toISOString(),
      });
    },

    // Self-talk entries (simplified)
    saveSelfTalkEntry: async (entry) => {
      try {
        const entries = simpleStorage.load('vibe_self_talk_entries') || [];
        const newEntry = {
          ...entry,
          id: Date.now(),
          timestamp: new Date().toISOString(),
        };
        const updatedEntries = [newEntry, ...entries];
        simpleStorage.save('vibe_self_talk_entries', updatedEntries);
        return newEntry;
      } catch (error) {
        console.error('Error saving self-talk entry:', error);
        throw error;
      }
    },

    getSelfTalkEntries: async () => {
      try {
        return simpleStorage.load('vibe_self_talk_entries') || [];
      } catch (error) {
        console.error('Error loading self-talk entries:', error);
        return [];
      }
    },

    // Future letters (simplified)
    saveFutureLetterExchange: async (exchange) => {
      try {
        const exchanges = simpleStorage.load('vibe_future_letters') || [];
        const updatedExchange = {
          ...exchange,
          savedAt: new Date().toISOString(),
        };
        const filteredExchanges = exchanges.filter(ex => ex.id !== exchange.id);
        const updatedExchanges = [updatedExchange, ...filteredExchanges];
        simpleStorage.save('vibe_future_letters', updatedExchanges);
        return updatedExchange;
      } catch (error) {
        console.error('Error saving future letter exchange:', error);
        throw error;
      }
    },

    getFutureLetterExchanges: async () => {
      try {
        return simpleStorage.load('vibe_future_letters') || [];
      } catch (error) {
        console.error('Error loading future letter exchanges:', error);
        return [];
      }
    },

    // Data management
    //
    // "Download My Data" has to include what people actually wrote. It used
    // to export results, progress and preferences only, while the page and
    // the Privacy Policy promised "everything you have written" - the tool
    // entries (letters, grief entries, pain logs) and saved insights were not
    // in the file.
    exportUserData: async () => {
      try {
        const tools = {};
        for (const toolKey of LEGACY_TOOL_KEYS) {
          const entry = loadToolData(toolKey);
          if (entry) tools[toolKey] = entry;
        }
        let savedInsights = [];
        let completedModules = state.completedModules;
        let moduleCompletedAt = {};
        try {
          const progress = useProgressStore.getState();
          savedInsights = progress.savedInsights || [];
          moduleCompletedAt = progress.moduleCompletedAt || {};
          // The record the dashboard shows, plus anything only this context
          // holds, once each.
          completedModules = [...new Set([...(state.completedModules || []), ...(progress.completedModules || [])])]
            .sort((a, b) => a - b);
        } catch { /* store not ready */ }
        return {
          metadata: {
            exportDate: new Date().toISOString(),
            version: '1.1',
            application: 'friendships-vibe',
          },
          data: {
            assessmentResults: state.assessmentResults,
            assessmentHistory: state.assessmentHistory,
            completedModules,
            moduleCompletedAt,
            completedTools: state.completedTools,
            userProgress: state.userProgress,
            preferences: state.preferences,
            savedInsights,
            tools,
          },
        };
      } catch (error) {
        console.error('Error exporting user data:', error);
        throw error;
      }
    },

    resetProgress: async (clearAll = false) => {
      try {
        if (clearAll) {
          // Clear all storage
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.startsWith('vibe_') || key.startsWith('friendships_')) {
              localStorage.removeItem(key);
            }
          });
        }
        dispatch({ type: ActionTypes.RESET_USER_DATA });
      } catch (error) {
        console.error('Error resetting progress:', error);
        throw error;
      }
    },

    // Preferences
    updatePreferences: (updates) => {
      dispatch({ type: ActionTypes.UPDATE_PREFERENCES, payload: updates });
    },

    getPreferences: () => {
      return state.preferences;
    },

    // Assessment data access
    getAssessmentResults: () => {
      console.log('📋 Getting assessment results from AppContext:', state.assessmentResults);
      return state.assessmentResults;
    },

    hasAssessmentResults: () => {
      const hasResults = !!state.assessmentResults;
      console.log('📋 AppContext has assessment results:', hasResults);
      return hasResults;
    },

    // Storage management
    getStorageStats: () => {
      return {
        totalSize: 'Unknown',
        itemCount: state.completedModules.length,
        categories: {
          'Assessment Data': state.assessmentResults ? 1 : 0,
          'Module Progress': state.completedModules.length,
          'Self-Talk Entries': 0,
          'Future Letters': 0,
        },
        encrypted: false,
        lastBackup: state.lastSync,
      };
    },
  };

  // CRITICAL FIX: Memoize context value to prevent infinite re-renders
  // Note: actions is recreated on every render which is intentional (closures over state/dispatch)
  // but the context value object itself should only change when state changes
  const contextValue = useMemo(() => ({
    // State
    ...state,

    // Actions (including scroll utilities)
    ...actions,
  }), [state]); // Only depend on state - actions will have fresh closures

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook for using the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;